import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import salesService from '../services/salesService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { formatDate, formatInstantDate } from '../utils/datetime';
import ConfirmActionModal from '../components/ConfirmActionModal';
import RegisterPaymentModal from '../components/RegisterPaymentModal';
import '../styles/general.css';

const SalesDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { timezone } = useContext(AuthContext);

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  const loadSale = useCallback(async () => {
    try {
      setLoading(true);
      const res = await salesService.getSaleById(id);
      setSale(res.data);
    } catch (err) {
      console.error('Error loading Sale:', err);
      notify.error(t('sales.detail.failedToLoadSale'));
    } finally {
      setLoading(false);
    }
  }, [id, notify, t]);

  useEffect(() => {
    loadSale();
  }, [loadSale]);

  const handleMarkInvoicedConfirm = async () => {
    setProcessing(true);
    try {
      const res = await salesService.markInvoiced(id);
      setSale(res.data);
      notify.success(t('sales.saleInvoiced'));
      setShowInvoiceModal(false);
    } catch (err) {
      console.error('Error marking sale as invoiced:', err);
      notify.error(t('sales.failedToMarkInvoiced'));
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };


  if (loading || !sale) {
    return (
      <div className="loading">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{t('sales.saleNumber')}: {sale.sale_number}</h1>
        <button
          onClick={() => navigate('/sales')}
          className="btn btn-secondary"
        >
          {t('sales.backToSales')}
        </button>
      </div>

      <fieldset className="form-fieldset">
        <legend>{t('sales.detail.generalData')}</legend>
        <div className="grid-2">
          <div>
            <label className="form-label">{t('sales.saleNumber')}</label>
            <p>{sale.sale_number}</p>
          </div>
          <div>
            <label className="form-label">{t('sales.detail.originQuoteNumber')}</label>
            <p>
              {sale.quote_number ? (
                <a
                  href={`/client-quotes/${sale.client_quote}/edit`}
                  onClick={(e) => { e.preventDefault(); navigate(`/client-quotes/${sale.client_quote}/edit`); }}
                >
                  {sale.quote_number}
                </a>
              ) : (
                t('sales.detail.noOriginQuote')
              )}
            </p>
          </div>
          <div>
            <label className="form-label">{t('table.client')}</label>
            <p>{sale.client_name}</p>
          </div>
          <div>
            <label className="form-label">RFC</label>
            <p>{sale.client_tax_id}</p>
          </div>
          <div>
            <label className="form-label">{t('table.buyer')}</label>
            <p>{sale.buyer_name}</p>
          </div>
          <div>
            <label className="form-label">{t('table.createdBy')}</label>
            <p>{sale.created_by_email}</p>
          </div>
          <div>
            <label className="form-label">{t('sales.detail.createdDate')}</label>
            <p>{formatInstantDate(sale.created_at, timezone)}</p>
          </div>
          <div>
            <label className="form-label">{t('common.status')}</label>
            <p>
              <span className={`table-status ${sale.status}`}>
                {t(`sales.status.${sale.status}`)}
              </span>
            </p>
          </div>
          <div>
            <label className="form-label">{t('sales.invoiced')}</label>
            <p>{sale.is_invoiced ? t('common.yes') : t('common.no')}</p>
          </div>
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('sales.detail.products')}</legend>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>{t('table.itemNumber')}</th>
                <th>{t('table.description')}</th>
                <th className="text-center">{t('table.quantity')}</th>
                <th className="text-right">{t('clientQuotes.unitPrice')}</th>
                <th className="text-right">{t('table.total')}</th>
              </tr>
            </thead>
            <tbody>
              {sale.products.map((product, index) => (
                <tr key={product.id}>
                  <td>{index + 1}</td>
                  <td>{product.description}</td>
                  <td className="text-center">{product.quantity}</td>
                  <td className="text-right">{formatCurrency(product.unit_price)}</td>
                  <td className="text-right">{formatCurrency(product.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>

      <div className="grid-2">
        <div />
        <div>
          <div className="flex-between">
            <span className="total-emphasis">{t('clientQuotes.subtotal')}</span>
            <span className="total-emphasis">{formatCurrency(sale.subtotal)}</span>
          </div>
          <div className="flex-between">
            <span>{t('clientQuotes.taxes')}</span>
            <span>{formatCurrency(sale.taxes)}</span>
          </div>
          <div className="flex-between">
            <span className="total-emphasis">{t('clientQuotes.grandTotal')}</span>
            <span className="total-emphasis">{formatCurrency(sale.total)}</span>
          </div>
          <div className="flex-between">
            <span>{t('sales.detail.paidAmount')}</span>
            <span>{formatCurrency(sale.paid_amount)}</span>
          </div>
          <div className="flex-between">
            <span>{t('payments.paymentAccount')}</span>
            <span>{sale.monetary_account_name || '—'}</span>
          </div>
          <div className="flex-between">
            <span>{t('payments.paidDate')}</span>
            {/* formatDate, not formatInstantDate: paid_date is a calendar
                date and carries no timezone to convert. */}
            <span>{sale.paid_date ? formatDate(sale.paid_date) : '—'}</span>
          </div>
        </div>
      </div>

      <div className="flex-center gap-15 mb-30">
        {sale.status === 'pendiente' && (
          <button
            onClick={() => setShowPayModal(true)}
            className="btn btn-primary"
          >
            {t('sales.markPaid')}
          </button>
        )}
        {!sale.is_invoiced && (
          <button
            onClick={() => setShowInvoiceModal(true)}
            className="btn btn-primary"
          >
            {t('sales.markInvoiced')}
          </button>
        )}
      </div>

      {/* A form rather than a confirmation: the payment now creates an
          accounting entry, which needs a destination and a date. */}
      <RegisterPaymentModal
        isOpen={showPayModal}
        document={sale && {
          id: sale.id,
          number: sale.sale_number,
          total: sale.total
        }}
        onSubmit={(payload) => salesService.markSalePaid(sale.id, payload)}
        onClose={() => setShowPayModal(false)}
        onPaid={() => {
          setShowPayModal(false);
          loadSale();
        }}
      />

      <ConfirmActionModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        onConfirm={handleMarkInvoicedConfirm}
        title={t('sales.markInvoiced')}
        message={t('sales.confirmMarkInvoiced')}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />
    </div>
  );
};

export default SalesDetailPage;
